/**
 * Service worker registration + update detection.
 *
 * Lifecycle on a fresh deploy:
 *   1. User opens the page → browser fetches /sw.js, sees new contents
 *   2. New SW installs in the background (cache-warming)
 *   3. New SW enters 'installed' state but stays 'waiting' because the
 *      old SW still controls the page
 *   4. We detect that and show an "Update available" prompt
 *   5. On user accept: post SKIP_WAITING to new SW, it activates, the
 *      'controllerchange' event fires, we reload the page
 */

type UpdateAvailableHandler = (apply: () => void) => void;

export async function registerServiceWorker(
  onUpdateAvailable: UpdateAvailableHandler,
): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  // In dev (Vite serves modules), the SW can cause stale-content headaches.
  // Skip registration on localhost so HMR works as expected.
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    console.info('[sw] skipping registration on localhost');
    return;
  }

  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

    // A waiting worker already exists (e.g. user came back to a page where
    // a new SW had previously finished installing) — surface immediately.
    if (reg.waiting && navigator.serviceWorker.controller) {
      notifyUpdate(reg.waiting, onUpdateAvailable);
    }

    reg.addEventListener('updatefound', () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          notifyUpdate(installing, onUpdateAvailable);
        }
      });
    });

    // When the new SW takes control, reload so the page is rendered by it.
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  } catch (err) {
    console.warn('[sw] registration failed', err);
  }
}

function notifyUpdate(worker: ServiceWorker, handler: UpdateAvailableHandler): void {
  handler(() => worker.postMessage({ type: 'SKIP_WAITING' }));
}
